/**
 * lib/views/javascripts/proto/voice/client.js — the voice-first realtime
 * client (ADR 0013): the browser half of lib/proto/voice/realtime-body.js,
 * ported from Vega's proven client and generalized for any Proto agent.
 *
 * Full-duplex conversation in the default chat (lib/views/htmls/proto/
 * chat.ejs): a Live button joins the composer; while live, the mic streams
 * PCM up the realtime socket, the agent's speech streams down, transcripts
 * render as chat bubbles, and the person can interrupt by VOICE (barge-in):
 * "mhm" is ignored, "wait/stop/<name>" (or any substantive speech) cancels
 * the reply and plays a short yield clip. Settings → Voice can pick the
 * COMPOSED engine instead: the mic stays open, a client VAD endpoints each
 * utterance, and it goes through the agent's own chat (transcribe → the
 * composer → a spoken reply).
 *
 * Typing and talking are one conversation: spoken turns go into
 * window.__m33chat, a live session is seeded from it, and the session id is
 * the chat's (__m33sid). The tuned constants (24 kHz, 50 ms batch, 100 ms
 * jitter buffer, 30 ms flush, 0.1 s lookahead, echo gate 2000 / 3 frames,
 * 1500 ms fallback) are Vega's — earned in production, preserved exactly.
 *
 * Contents: view
 * Ontology: Code object M33C-1018 (immutable ref — assigned once, never edit)
 * Team: J. Brant Arseneau & the Mach33 Agents.
 */

import { SAMPLE_RATE, int16ToBase64, base64ToInt16, peak, resampleTo24k, wavFromFrames } from './audio.js';
import { classifier } from './barge-in.js';

// The mic capture processor. It runs in the AudioWorklet's own global scope,
// so it is loaded from a Blob URL rather than imported.
const PCM_WORKLET = 'class ProtoPCM extends AudioWorkletProcessor{process(inputs){var input=inputs[0]&&inputs[0][0];if(!input)return true;var i16=new Int16Array(input.length);for(var i=0;i<input.length;i++){var s=Math.max(-1,Math.min(1,input[i]));i16[i]=s<0?s*0x8000:s*0x7FFF;}this.port.postMessage(i16.buffer,[i16.buffer]);return true;}}registerProcessor("proto-pcm",ProtoPCM);';

// ── Mount ────────────────────────────────────────────────────────────────
// CFG: { apiBase, backchannelRx, stopRx } from lib/proto/voice/voice-view.js.
export function mountVoice(CFG, doc = document, win = window) {
  const form = doc.getElementById('m33-chat-form');
  const scroll = doc.getElementById('m33-chat-scroll');
  if (!form || !scroll) return;

  const PCM_INITIAL_BUFFER_MS = 100, PCM_BATCH_MS = 50, PCM_FLUSH_MS = 30, PCM_SCHEDULE_AHEAD = 0.10;
  const PCM_BATCH_SAMPLES = Math.floor(SAMPLE_RATE * PCM_BATCH_MS / 1000);
  const ECHO_THRESHOLD = 2000, ECHO_FRAMES = 3, INTERRUPT_FALLBACK_MS = 1500;
  const classify = classifier(CFG.backchannelRx, CFG.stopRx);

  const rt = {
    ws: null, audioCtx: null, micStream: null, micWorklet: null,
    live: false, nextStartTime: 0, playbackQueue: [],
    pendingUser: null, pendingUserText: '', pendingUserItem: null, userPrefix: '',
    lastUserBubble: null, lastUserBubbleAt: 0, lastFinalText: '', lastFinalAt: 0,
    pendingAssistant: null, pendingAssistantText: '',
    interruptionPending: false, interruptionTimer: null,
    yieldBank: [], yieldIndex: 0, echoLoud: 0, seeded: false
  };
  let pcmAccum = [], pcmAccumSamples = 0, pcmInitialHold = false, pcmInitialTimer = null, pcmFlushTimer = null;

  // ── UI: the Live button (joins the composer) ───────────────────────────
  // Its live look (.m33-chat-ctl.live-on) is in public/m33-static/proto-chat.css.
  const live = doc.createElement('button');
  live.type = 'button'; live.id = 'm33-chat-live'; live.className = 'm33-chat-ctl';
  live.title = 'Live voice conversation'; live.setAttribute('aria-pressed', 'false');
  live.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 10v4M8 6v12M12 9v6M16 4v16M20 10v4"/></svg>';
  // Where it joins: the composer's action row (lib/proto/composer.js), with the
  // chat's other voice rails and before the spacer that pushes Send to the far
  // end; on a plain flat form, beside Send as before. Send is NOT a child of the
  // form in the composer's box, so each anchor is used only with its own parent
  // — insertBefore throws on a foreign one, and that took the whole voice
  // client down with it. No anchor at all is said, never thrown.
  const sendBtn = doc.getElementById('m33-chat-send');
  const row = form.querySelector('.m33-tc-actions');
  const spacer = row ? row.querySelector('.m33-tc-cspace') : null;
  if (row) row.insertBefore(live, spacer && spacer.parentNode === row ? spacer : null);
  else if (sendBtn && sendBtn.parentNode) sendBtn.parentNode.insertBefore(live, sendBtn);
  else {
    console.warn('[proto:voice:view] no composer action row and no Send button to mount the Live button beside — appended to the form');
    form.appendChild(live);
  }
  function paintLive() { live.classList.toggle('live-on', rt.live); live.setAttribute('aria-pressed', rt.live ? 'true' : 'false'); }

  function bubble(role) {
    const d = doc.createElement('div');
    d.className = 'm33-chat-msg ' + role;
    scroll.appendChild(d); scroll.scrollTop = scroll.scrollHeight;
    return d;
  }
  function sys(text) {
    const d = bubble('assistant'); d.style.opacity = '0.6'; d.textContent = text;
  }

  // ── Audio plumbing ─────────────────────────────────────────────────────
  function ensureAudioCtx() {
    if (rt.audioCtx) return Promise.resolve();
    const Ctx = win.AudioContext || win.webkitAudioContext;
    try { rt.audioCtx = new Ctx({ sampleRate: SAMPLE_RATE }); }
    catch (_) { rt.audioCtx = new Ctx(); }
    const url = win.URL.createObjectURL(new win.Blob([PCM_WORKLET], { type: 'application/javascript' }));
    return rt.audioCtx.audioWorklet.addModule(url);
  }

  function agentIsSpeaking() {
    return !!rt.pendingAssistant || (rt.audioCtx && rt.audioCtx.currentTime < rt.nextStartTime - 0.05);
  }

  function scheduleChunk(i16) {
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
    const buf = rt.audioCtx.createBuffer(1, f32.length, SAMPLE_RATE);
    buf.getChannelData(0).set(f32);
    const src = rt.audioCtx.createBufferSource();
    src.buffer = buf; src.connect(rt.audioCtx.destination);
    const now = rt.audioCtx.currentTime;
    const startAt = Math.max(now + PCM_SCHEDULE_AHEAD, rt.nextStartTime);
    src.start(startAt);
    rt.nextStartTime = startAt + buf.duration;
    rt.playbackQueue.push(src);
    src.onended = function () { const ix = rt.playbackQueue.indexOf(src); if (ix >= 0) rt.playbackQueue.splice(ix, 1); };
  }
  function flushAccum() {
    if (pcmFlushTimer) { win.clearTimeout(pcmFlushTimer); pcmFlushTimer = null; }
    if (!pcmAccumSamples) return;
    const merged = new Int16Array(pcmAccumSamples);
    let off = 0;
    for (let i = 0; i < pcmAccum.length; i++) { merged.set(pcmAccum[i], off); off += pcmAccum[i].length; }
    pcmAccum = []; pcmAccumSamples = 0;
    scheduleChunk(merged);
  }
  function playPCM(i16) {
    pcmAccum.push(new Int16Array(i16)); pcmAccumSamples += i16.length;
    const queueEmpty = rt.nextStartTime <= rt.audioCtx.currentTime + 0.01;
    if (queueEmpty && !pcmInitialHold) {
      pcmInitialHold = true;
      pcmInitialTimer = win.setTimeout(() => { pcmInitialHold = false; flushAccum(); }, PCM_INITIAL_BUFFER_MS);
      return;
    }
    if (pcmInitialHold) return;
    if (pcmAccumSamples >= PCM_BATCH_SAMPLES) flushAccum();
    else if (!pcmFlushTimer) pcmFlushTimer = win.setTimeout(flushAccum, PCM_FLUSH_MS);
  }
  function cancelPlayback() {
    for (let i = 0; i < rt.playbackQueue.length; i++) { try { rt.playbackQueue[i].stop(); } catch (_) {} }
    rt.playbackQueue = [];
    rt.nextStartTime = rt.audioCtx ? rt.audioCtx.currentTime : 0;
    pcmAccum = []; pcmAccumSamples = 0; pcmInitialHold = false;
    if (pcmInitialTimer) { win.clearTimeout(pcmInitialTimer); pcmInitialTimer = null; }
    if (pcmFlushTimer) { win.clearTimeout(pcmFlushTimer); pcmFlushTimer = null; }
  }
  function playClip(b64mp3) {
    try {
      const bin = atob(b64mp3), u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      rt.audioCtx.decodeAudioData(u8.buffer, (buf) => {
        const src = rt.audioCtx.createBufferSource();
        src.buffer = buf; src.connect(rt.audioCtx.destination);
        const startAt = Math.max(rt.audioCtx.currentTime + 0.02, rt.nextStartTime);
        src.start(startAt);
        rt.nextStartTime = startAt + buf.duration;
      });
    } catch (_) {}
  }

  // ── Barge-in ───────────────────────────────────────────────────────────
  function yieldAndCancel() {
    cancelPlayback();
    if (rt.yieldBank.length) {
      const yc = rt.yieldBank[rt.yieldIndex % rt.yieldBank.length]; rt.yieldIndex++;
      if (yc && yc.audio) playClip(yc.audio);
    }
    rt.pendingAssistant = null; rt.pendingAssistantText = '';
    send({ type: 'response.cancel' });
  }

  // ── Transcript bubbles ─────────────────────────────────────────────────
  // One bubble per THOUGHT, not per VAD segment: Whisper finalizes a segment
  // at every pause ("uh", "um" → 900ms silence), so consecutive segments
  // within 3s merge into the same bubble (Vega's merge window), the same
  // item_id always reuses its bubble, and identical finals within 5s dedup.
  function beginUserSegment(itemId) {
    const now = Date.now();
    if (rt.pendingUser && rt.pendingUserItem === itemId) return;   // same segment continues
    if (rt.pendingUser) {                                          // new item mid-bubble: roll text into prefix
      rt.userPrefix = rt.pendingUser.textContent ? rt.pendingUser.textContent + ' ' : '';
    } else if (rt.lastUserBubble && (now - rt.lastUserBubbleAt) < 3000) {
      rt.pendingUser = rt.lastUserBubble;                          // merge window: continue the thought
      rt.userPrefix = rt.pendingUser.textContent ? rt.pendingUser.textContent + ' ' : '';
    } else {
      rt.pendingUser = bubble('user'); rt.userPrefix = '';
    }
    rt.pendingUserItem = itemId || null; rt.pendingUserText = '';
  }
  function appendUser(delta, itemId) {
    beginUserSegment(itemId);
    rt.pendingUserText += delta;
    rt.pendingUser.textContent = rt.userPrefix + rt.pendingUserText;
    scroll.scrollTop = scroll.scrollHeight;
  }
  function completeUser(fullText, itemId) {
    const now = Date.now();
    if (fullText === rt.lastFinalText && (now - rt.lastFinalAt) < 5000) return;   // duplicate final
    beginUserSegment(itemId);
    // Whisper refines CUMULATIVELY: when the new final extends what the bubble
    // already says, replace rather than repeat ("Why is Why isn't my…").
    const prev = rt.userPrefix.trim();
    if (prev && fullText.slice(0, prev.length) === prev) rt.userPrefix = '';
    rt.pendingUser.textContent = rt.userPrefix + fullText;
    rt.lastFinalText = fullText; rt.lastFinalAt = now;
    rt.lastUserBubble = rt.pendingUser; rt.lastUserBubbleAt = now;
    rt.pendingUser = null; rt.pendingUserItem = null; rt.pendingUserText = ''; rt.userPrefix = '';
  }
  function appendAssistant(delta) {
    if (!rt.pendingAssistant) {
      rt.pendingAssistant = bubble('assistant'); rt.pendingAssistantText = '';
      // The agent replied — the person's thought is closed. Commit it to the
      // SHARED conversation so typed chat knows what was said aloud.
      if (rt.lastUserBubble && rt.lastUserBubble.textContent && win.__m33chat) {
        try { win.__m33chat.push('user', rt.lastUserBubble.textContent); } catch (_) {}
      }
      rt.lastUserBubble = null;
    }
    rt.pendingAssistantText += delta;
    rt.pendingAssistant.textContent = rt.pendingAssistantText;
    scroll.scrollTop = scroll.scrollHeight;
  }
  function finaliseAssistant() {
    if (rt.pendingAssistant && rt.pendingAssistantText) {
      if (win.__m33md) { try { rt.pendingAssistant.innerHTML = win.__m33md(rt.pendingAssistantText); } catch (_) {} }
      // Commit what was actually spoken (truncated-to-spoken on interrupts)
      // to the shared conversation.
      if (win.__m33chat) { try { win.__m33chat.push('assistant', rt.pendingAssistantText); } catch (_) {} }
    }
    rt.pendingAssistant = null; rt.pendingAssistantText = '';
  }

  // ── WebSocket + events ─────────────────────────────────────────────────
  function send(obj) { try { if (rt.ws && rt.ws.readyState === 1) rt.ws.send(JSON.stringify(obj)); } catch (_) {} }

  function handleEvent(evt) {
    const t = evt.type;
    if (t === 'session.updated' && !rt.seeded) {
      // Seed the live session with the recent SHARED conversation (typed +
      // previously spoken turns), so talking continues the same exchange
      // instead of starting an amnesiac one.
      rt.seeded = true;
      try {
        const hist = win.__m33chat ? win.__m33chat.history().slice(-12) : [];
        for (let i = 0; i < hist.length; i++) {
          const m = hist[i];
          if (!m || !m.content) continue;
          send({ type: 'conversation.item.create', item: { type: 'message', role: m.role,
            content: [{ type: m.role === 'user' ? 'input_text' : 'text', text: String(m.content).slice(0, 2000) }] } });
        }
      } catch (_) {}
      return;
    }
    if (t === 'response.output_audio.delta') { if (evt.delta) playPCM(base64ToInt16(evt.delta)); return; }
    if (t === 'response.output_audio_transcript.delta' || t === 'response.text.delta') { if (evt.delta) appendAssistant(evt.delta); return; }
    if (t === 'response.done') { finaliseAssistant(); return; }
    if (t === 'response.cancelled') { finaliseAssistant(); return; }
    if (t === 'conversation.item.input_audio_transcription.delta') {
      if (rt.interruptionPending) {
        rt.interruptionPending = false;
        if (rt.interruptionTimer) { win.clearTimeout(rt.interruptionTimer); rt.interruptionTimer = null; }
        const intent = classify(evt.delta);
        if (intent !== 'backchannel') yieldAndCancel();
      }
      if (evt.delta) appendUser(evt.delta, evt.item_id);
      return;
    }
    if (t === 'conversation.item.input_audio_transcription.completed') { if (evt.transcript) completeUser(evt.transcript, evt.item_id); return; }
    if (t === 'input_audio_buffer.speech_started') {
      if (agentIsSpeaking() && !rt.interruptionPending) {
        rt.interruptionPending = true;
        rt.interruptionTimer = win.setTimeout(() => {
          rt.interruptionPending = false;
          yieldAndCancel();   // no transcript arrived — assume a real interruption
        }, INTERRUPT_FALLBACK_MS);
      }
      return;
    }
    if (t === 'vega.dynamics_filler') { if (evt.audio) playClip(evt.audio); return; }
    if (t === 'vega.dynamics_yield_bank') { rt.yieldBank = evt.bank || []; return; }
    if (t === 'vega.tool_executed') { if (evt.summary) sys('⚙ ' + evt.summary); return; }
    if (t === 'vega.proxy.upstream_reconnect') { cancelPlayback(); return; }
    if (t === 'vega.proxy.upstream_failed') { cancelPlayback(); sys('Voice connection lost.'); stopLive(); return; }
    if (t === 'error') { const msg = (evt.error && evt.error.message) || 'voice error'; sys('⚠ ' + msg); }
  }

  // ── Mic ────────────────────────────────────────────────────────────────
  function startMic() {
    const nav = win.navigator;
    if (!nav.mediaDevices || !nav.mediaDevices.getUserMedia) {
      return Promise.reject(new Error(win.isSecureContext === false
        ? 'insecure context — the microphone needs HTTPS (or localhost)'
        : 'this browser does not expose a microphone'));
    }
    return nav.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    }).then((stream) => {
      rt.micStream = stream;
      const src = rt.audioCtx.createMediaStreamSource(stream);
      rt.micWorklet = new win.AudioWorkletNode(rt.audioCtx, 'proto-pcm');
      rt.micWorklet.port.onmessage = function (e) {
        if (!rt.live) return;
        let i16 = resampleTo24k(new Int16Array(e.data), rt.audioCtx.sampleRate);
        // Echo gate: while the agent speaks, only forward genuinely loud frames
        // (3 consecutive) so its own voice doesn't trigger VAD barge-in.
        if (agentIsSpeaking()) {
          if (peak(i16) < ECHO_THRESHOLD) { rt.echoLoud = 0; i16 = new Int16Array(i16.length); }
          else { rt.echoLoud++; if (rt.echoLoud < ECHO_FRAMES) i16 = new Int16Array(i16.length); }
        } else { rt.echoLoud = 0; }
        send({ type: 'input_audio_buffer.append', audio: int16ToBase64(i16.buffer) });
      };
      src.connect(rt.micWorklet);
    });
  }
  function stopMic() {
    if (rt.micStream) { rt.micStream.getTracks().forEach((tr) => { tr.stop(); }); rt.micStream = null; }
    rt.micWorklet = null;
  }

  // ── Session lifecycle ──────────────────────────────────────────────────
  function sessionId() {
    if (win.__m33sid) return win.__m33sid;   // same id as typed chat → one grouped conversation in the store
    const k = 'm33proto:voice-session';
    let v = win.sessionStorage.getItem(k);
    if (!v) { v = 'pv-' + Math.random().toString(36).slice(2) + Date.now().toString(36); win.sessionStorage.setItem(k, v); }
    return v;
  }
  win.__m33voiceEngine = 'realtime';   // settings panel shows the active tier

  // ── COMPOSED engine ────────────────────────────────────────────────────
  // Hands-free conversation with the agent's own chat brain (ADR 0013): mic
  // stays open, client VAD endpoints each utterance, STT (Deepgram/xAI
  // server-side) → the EXISTING chat pipeline (Claude, memory, markdown,
  // spoken reply with the synced typewriter). Barge-in: speak while the reply
  // is playing → TTS stops, your utterance captures.
  const co = { active: false, stream: null, worklet: null, speech: false, loud: 0,
    silentMs: 0, speechMs: 0, frames: [], pre: [], preSamples: 0, busy: false };
  function coPrefs() { try { return (win.__m33voicePrefs && win.__m33voicePrefs.get()) || {}; } catch (_) { return {}; } }
  function coEndUtterance() {
    const frames = co.frames, ms = co.speechMs;
    co.speech = false; co.frames = []; co.loud = 0; co.silentMs = 0; co.speechMs = 0;
    if (ms < 300 || co.busy) return;                    // noise blip, or a turn already in flight
    co.busy = true;
    win.fetch((CFG.apiBase || '') + '/api/voice/transcribe', { method: 'POST', headers: { 'Content-Type': 'audio/wav' }, body: wavFromFrames(frames) })
      .then((r) => r.json())
      .then((j) => {
        co.busy = false;
        const text = ((j && j.text) || '').trim();
        if (!text) return;
        const input = doc.getElementById('m33-chat-input');
        if (win.__m33tts) win.__m33tts.arm();     // the reply comes back spoken
        input.value = text; form.requestSubmit();
      })
      .catch(() => { co.busy = false; });
  }
  function coOnFrame(i16) {
    const speaking = win.__m33tts && win.__m33tts.isSpeaking();
    const p = peak(i16), frameMs = (i16.length / SAMPLE_RATE) * 1000;
    if (!co.speech) {
      co.pre.push(i16); co.preSamples += i16.length;                       // ~600ms pre-roll
      while (co.preSamples > SAMPLE_RATE * 0.6) { const f = co.pre.shift(); co.preSamples -= f.length; }
      const th = speaking ? ECHO_THRESHOLD : 1200;                          // stricter gate over our own audio
      const need = speaking ? 8 : 10;                                       // ~40–55ms of sustained speech
      if (p >= th) {
        co.loud++;
        if (co.loud >= need) {
          if (speaking && win.__m33tts) win.__m33tts.stop();               // BARGE-IN: cut the reply
          co.speech = true; co.frames = co.pre.slice(); co.pre = []; co.preSamples = 0;
          co.silentMs = 0; co.speechMs = 0;
        }
      } else co.loud = 0;
    } else {
      co.frames.push(i16); co.speechMs += frameMs;
      if (p < 800) {
        co.silentMs += frameMs;
        const eot = Number(coPrefs().eotMs) || 900;                         // end-of-turn pause (settings)
        if (co.silentMs >= eot) coEndUtterance();
      } else co.silentMs = 0;
    }
  }
  function coStart() {
    win.__m33voiceEngine = 'composed';
    ensureAudioCtx().then(() => win.navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } }))
      .then((stream) => {
        co.stream = stream;
        const src = rt.audioCtx.createMediaStreamSource(stream);
        co.worklet = new win.AudioWorkletNode(rt.audioCtx, 'proto-pcm');
        co.worklet.port.onmessage = function (e) { if (!co.active) return; coOnFrame(resampleTo24k(new Int16Array(e.data), rt.audioCtx.sampleRate)); };
        src.connect(co.worklet);
        co.active = true; rt.live = true; paintLive();
        if (win.__m33tts) win.__m33tts.arm();
      }).catch((e) => {
        const why = (e && e.name === 'NotAllowedError') ? 'permission denied — allow the microphone and try again'
          : (e && e.message) || 'unavailable';
        sys('Microphone: ' + why); coStop();
      });
  }
  function coStop() {
    co.active = false; rt.live = false; paintLive();
    if (co.stream) { co.stream.getTracks().forEach((t) => { t.stop(); }); co.stream = null; }
    co.worklet = null; co.speech = false; co.frames = []; co.pre = []; co.preSamples = 0;
    win.__m33voiceEngine = 'realtime';
  }

  // ── Speech-to-speech engine ────────────────────────────────────────────
  function startLive() {
    rt.seeded = false;   // fresh connection seeds the shared history again
    ensureAudioCtx().then(() => {
      const proto = win.location.protocol === 'https:' ? 'wss://' : 'ws://';
      let prefVoice = '';
      // Only xAI voice ids ride the Grok session; an ElevenLabs selection
      // applies to the composed/typewriter paths, not speech-to-speech.
      try {
        const vp = win.__m33voicePrefs && win.__m33voicePrefs.get();
        if (vp && vp.voiceId && vp.ttsProvider !== 'elevenlabs') prefVoice = '&voice=' + encodeURIComponent(vp.voiceId);
      } catch (_) {}
      rt.ws = new win.WebSocket(proto + win.location.host + (CFG.apiBase || '') + '/api/realtime?session_id=' + encodeURIComponent(sessionId()) + prefVoice);
      rt.ws.onmessage = function (e) {
        if (typeof e.data !== 'string') return;
        let evt;
        try { evt = JSON.parse(e.data); } catch (_) { return; }
        handleEvent(evt);
      };
      rt.ws.onclose = function () { if (rt.live) stopLive(); };
      rt.ws.onopen = function () {
        startMic().then(() => { rt.live = true; paintLive(); })
          .catch((e) => {
            const why = (e && e.name === 'NotAllowedError') ? 'permission denied — allow the microphone and try again'
              : (e && e.name === 'NotFoundError') ? 'no microphone found'
                : (e && e.message) || 'unavailable';
            sys('Microphone: ' + why);
            stopLive();
          });
      };
    }).catch(() => { sys('Audio unavailable in this browser.'); });
  }
  function stopLive() {
    rt.live = false; paintLive();
    send({ type: 'response.cancel' });
    if (rt.ws) { try { rt.ws.close(1000); } catch (_) {} rt.ws = null; }
    stopMic(); cancelPlayback();
    finaliseAssistant();
    if (rt.interruptionTimer) { win.clearTimeout(rt.interruptionTimer); rt.interruptionTimer = null; }
  }
  live.addEventListener('click', () => {
    if (rt.live) { if (co.active) coStop(); else stopLive(); return; }
    // Engine choice comes from Settings → Voice: speech-to-speech (Grok) or
    // composed (the agent's own chat brain, hands-free).
    if (coPrefs().engine === 'composed') coStart(); else startLive();
  });
  win.addEventListener('pagehide', () => { if (co.active) coStop(); else if (rt.live) stopLive(); });
}
